<template>
    <div class="clients__page--view mb--35">
        <div class="main__content">
            <template v-if="isAllowed">
                <EmptyPage 
                    state="empty_custom" 
                    title="Upgrade your account"
                    subtitle="You have exceeded the allowed number of invoices on your account plan. To create more invoices, upgrade your account plan in your account settings."
                    icon-name="lock"
                    @action="goToSettings" 
                />
            </template>

            <template v-else>
                <InvoiceForm 
                    v-model="defaultInvoice" 
                    :default-client="defaultClient"
                    :default-project="defaultProject"
                    :billing-info-data="billingInfoData"
                    :invoice-type="invoiceType"
                    @open-modal="modalState = $event"
                    @save-complete="onSaveComplete"
                    @send-complete="onSendComplete"
                    @initiate-preview="v => previewInvoiceData = v"
                />
            </template>
        </div>

        <CreateClientModal 
            :show-modal="modalState === 'createClientModal'" 
            action-type="add" 
            :loading="loadingState === 'createClientLoading'"
            :show-billing-input="false"
            @cancel="modalState = ''" 
            @complete="createNewClient"
        />

        <CreateProjectModal 
            :show-modal="modalState === 'createProjectModal'" 
            action-type="add"
            :loading="loadingState === 'createProjectLoading'"
            :current-selection="modalState === 'createProjectModal' ? 'create_paid' : ''"
            @complete="onProjectComplete"
            @close="modalState = ''" 
        />

        <MainModal 
            :show-modal="modalState === 'showAddTaxModal'" 
            size="sm" 
            title="Manage Tax"
            @close="modalState = ''"
        >
            <template #body>
                <div>
                    <NonLinkedTabs 
                        :show-count="false" 
                        :list="taxHeaderList" 
                        type="pill" 
                        position="end" 
                        @select="v => selectedTaxHeader = v.name"
                    >
                        <template #nonLinkedTab="tab">
                            <NonLinkedTab :is-active="tab.name === 'Add new tax'">
                                <div class="mt--10 invoice__manage__tax">
                                    <div class="w-100">
                                        <FormLabel title="Tax Name" font-size="text--sm" />
                                        <select v-model="invoiceTaxType.name" class="form-select form-select-sm w-100 text--capitalize">
                                            <option v-for="taxName in taxTypes" :key="taxName.id" :value="taxName.id">
                                                {{ taxName.name }}
                                            </option>
                                        </select>
                                    </div>
        
                                    <div class="w-100 mt--10">
                                        <FormLabel title="Tax Type" font-size="text--sm" />
                                        <select v-model="invoiceTaxType.type" class="form-select form-select-sm w-100 text--capitalize">
                                            <option v-for="tax in ['percentage', 'flat']" :key="tax" :value="tax">
                                                {{ tax }}
                                            </option>
                                        </select>
                                    </div>
                
                                    <div class="mt--10 w-100">
                                        <FormLabel title="Tax Value" font-size="text--sm" />
                                        <div class="w-100">
                                            <money 
                                                v-if="invoiceTaxType.type === 'flat'"
                                                v-model="invoiceTaxType.value" 
                                                v-bind="config" 
                                                class="form-control form-control-sm w-100" 
                                                spellcheck="false" 
                                                @keydown.native="preventKeys"
                                            />
                                            <input-money 
                                                v-else
                                                v-model="invoiceTaxType.value" 
                                                class="form__input form-control-border-solid form-control-sm w-100" 
                                                prefix="%"
                                                precision="2" 
                                            /> 
                                        </div>
                                    </div>
                                </div>
                            </NonLinkedTab>

                            <NonLinkedTab :is-active="tab.name === 'All added taxes'">
                                <div class="invoice__manage__tax">
                                    <template v-if="!defaultInvoice.meta.tax.length">
                                        <EmptyPage 
                                            state="empty_custom" 
                                            title="No added taxes yet." 
                                            icon-name="money" 
                                            subtitle="Add a tax item to see it here."
                                        />
                                    </template>
                                    <template v-else>
                                        <ul class="mt--30 invoice__manage__tax__list">
                                            <li v-for="(item, index) in defaultInvoice.meta.tax" :key="item._id" class="flex align-items-center mb--5 w-100">
                                                <div class="flex align-items-center justify-content-between w-100">
                                                    <div class="flex align-items-center">
                                                        <span class="box-icon block" :class="[item.taxType]" />
                                                        <span class="text--sm ml--5">{{ mapTaxTypes[item.taxName] }}, {{ item.taxType }}, {{ computeTaxValue(item) }}</span>
                                                    </div>
                                                    <span class="pointer inline-block ml--5" @click.stop="removeTaxItem(index)">
                                                        <IconSvg name="close" width="16px" fill="#212529" />
                                                    </span>
                                                </div>
                                            </li>
                                        </ul>
                                    </template>
                                </div>
                            </NonLinkedTab>
                        </template>
                    </NonLinkedTabs>
                </div>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <template v-if="selectedTaxHeader === 'Add new tax'">
                        <BaseButton
                            label="Cancel"
                            variant="outline" 
                            class=" mr--5" 
                            @submit="modalState = ''"  
                        />
                        <BaseButton 
                            label="Add"
                            :disabled="addTaxDisabled" 
                            @submit="addTaxToInvoice" 
                        />
                    </template>
                    <template v-else>
                        <BaseButton 
                            label="Close" 
                            @submit="modalState = ''"  
                        />
                    </template>
                </div>
            </template>
        </MainModal>

        <ManageRemindersModal 
            :initial-reminders="defaultInvoice.meta.reminders" 
            :show-modal="modalState === 'showRemindersModal'"
            @cancel="modalState = ''" 
            @complete="manageReminders"
        />

        <MainModal 
            :show-modal="modalState === 'showPaymentInfoModal'" 
            size="sm" 
            title="Set payment method"
            @close="modalState = ''"
        >
            <template #body>
                <form>
                    <div class="client__modal__body">
                        <div class="form--control">
                            <FormLabel title="Billing method" />
                            <CustomDropdown 
                                id="billingMethods"
                                v-model="form.selectedBillingMethod" 
                                class="w-100" 
                                size="md"
                                shadow
                                custom-text
                                list-type="billing methods" 
                                index-label="name" 
                                :options="billingMethods"
                                @select="setBillingMethod"
                            >
                                <template #text="{ item }">
                                    <div class="flex align-items-center w-100">
                                        <span class="dropdown--item-label mr--10">
                                            {{ item.name }}
                                        </span>
                                        <span v-if="!item.plans.includes(accountPlan)" class="tag--pink text--xs tag bold badge rounded-pill">
                                            Plus plan only
                                        </span>
                                    </div>
                                </template>
                            </CustomDropdown>
                        </div> 

                        <template v-if="form.selectedBillingMethod.id === 'personal_account'">
                            <div class="flex align-items-center justify-content-between mt--20">
                                <FormLabel title="Select existing bank account" />
                                <BaseButton 
                                    variant="ghost"
                                    label="+ Add account" 
                                    bold
                                    class="p--0"
                                    @submit="openAccountModal" 
                                />
                            </div>

                            <CustomDropdown 
                                id="accountDropdown" 
                                v-model="accountDetails" 
                                :options="userAccounts"
                                list-type="accounts" 
                                index-label="accountName" 
                                custom-text
                                :searchable="false"
                                @select="setAccount" 
                            >
                                <template #text="{ item }">
                                    <span class="pointer inline-block" @click.stop="$emit('remove', item)">
                                        <IconSvg name="bank" fill="#6a7381" />
                                    </span>
                                    <span class="ml--10 text--sm">
                                        {{ item.accountName }}
                                    </span>
                                </template>
                            </CustomDropdown>
                        </template>

                        <template v-if="form.selectedBillingMethod.id === 'payment_link'">
                            <div class="flex align-items-center justify-content-between mt--15">
                                <FormLabel title="Select a payment link" />
                                <BaseButton 
                                    variant="ghost"
                                    variant-style="primary"
                                    label="+ Add link" 
                                    bold
                                    class="p--0"
                                    @submit="openLinkModal" 
                                />
                            </div>

                            <CustomDropdown 
                                id="paymentLinkDropdown" 
                                v-model="form.linkData" 
                                :options="userPaymentLinks"
                                list-type="payment links" 
                                index-label="name" 
                                custom-text
                                :searchable="false"
                                @select="setLinkData" 
                            >
                                <template #text="{ item }">
                                    <div class="flex align-items-center">
                                        <IconSvg name="link" fill="#212529" />
                                        <span class="ml--10 text--sm">
                                            {{ item.name }} payment link 
                                        </span>
                                    </div>
                                </template>
                            </CustomDropdown>
                        </template>
                    </div>
                </form>
            </template>
            
            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton
                        class="mr--5" 
                        label="Cancel"
                        variant="outline" 
                        @submit="modalState = ''"
                    />
                    <BaseButton label="Update" :disabled="billingMethodSelectionBtn" @submit="updateLinks" />
                </div>
            </template>
        </MainModal>

        <MainModal 
            :show-modal="modalState === 'showAddLinkModal'" 
            size="sm" 
            title="Add new payment link"
            @close="closeLinkModal"
        >
            <template #body>
                <form>
                    <div class="client__modal__body">
                        <div class="mb--15">
                            <FormLabel title="Payment Link Provider" html-for="info" />
                            <select v-model="linkData.name" class="form-select form-select-sm text--sm">
                                <option v-for="link in providers" :key="link" :value="link">
                                    {{ link }}
                                </option>
                            </select>
                        </div>
                    
                        <FormInput 
                            id="linkUrl" 
                            v-model="linkData.url"
                            size="sm" 
                            info-type="required"
                            no-margin-bottom
                            label="Link URL"
                        />
                    </div>
                </form>
            </template>
            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton
                        class="mr--5" 
                        label="Cancel"
                        variant="outline" 
                        :disabled="loadingState === 'updateLinkLoading'"
                        @submit="closeLinkModal"
                    />
                    <BaseButton 
                        label="Add link" 
                        :disabled="!linkData.name || !linkData.url" 
                        :loading="loadingState === 'updateLinkLoading'"
                        @submit="updatePaymentLinks" 
                    />
                </div>
            </template>
        </MainModal>

        <UpgradeAccountModal 
            :show-modal="modalState === 'showUpgradeModal'" 
            :type="upgradeField" 
            @cancel="closeUpgradeModal" 
        />

        <PreviewInvoice v-if="previewInvoiceData" :invoice-data="previewInvoiceData" />

        <MainModal
            :show-modal="modalState === 'showCCEmailsModal'" 
            size="sm" 
            title="Add corresponding emails"
            @close="closeCCEmails"
        >
            <template #body>
                <form @submit.prevent="">
                    <div class="form-check mb--15">
                        <input 
                            id="flexCheckDefault" 
                            v-model="addClientsEmails" 
                            class="form-check-input" 
                            type="checkbox" 
                            @change="toggleBatchClientEmails"
                        >
                        <label class="form-check-label text--xs" for="addClientsEmails">
                            Add corresponding emails from currently selected client
                        </label>
                    </div>

                    <MultipleBillingEmails
                        :is-flex="false" 
                        :items="innerTempEmails" 
                        classes="w-100"
                        @add="addEmail" 
                        @delete="removeEmail"
                    />
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <Base-Button
                        class="mr--5" 
                        label="Cancel"
                        variant="outline" 
                        @submit="closeCCEmails"
                    />
                    <Base-Button label="Update" @submit="updateCCEmails" />
                </div>
            </template>
        </MainModal>

        <MainModal 
            :show-modal="modalState === 'showAddAccountModal'" 
            size="sm" 
            title="Add new account"
            @close="closeAccountModal"
        >
            <template #body>
                <form>
                    <template v-if="accountInfoByCurrency">
                        <FormInput 
                            v-if="accountInfoByCurrency['bankName']"
                            id="bankName" 
                            v-model="accountDetails.bankName"
                            size="sm" 
                            info-type="required" 
                            label="Bank Name"
                        />

                        <FormInput 
                            v-if="accountInfoByCurrency['accountName']"
                            id="accountName" v-model="accountDetails.accountName"
                            size="sm" info-type="required" label="Account Name" 
                        />

                        <FormInput 
                            v-if="accountInfoByCurrency['accountNumber']"
                            id="accountNumber" v-model="accountDetails.accountNumber"
                            size="sm" info-type="required" label="Account Number"
                        />
    
                        <FormInput 
                            v-if="accountInfoByCurrency['swiftCode']"
                            id="swift" v-model="accountDetails.swiftCode"
                            size="sm" label="SWIFT/BIC"
                        />

                        <FormInput 
                            v-if="accountInfoByCurrency['sortCode']"
                            id="swift" v-model="accountDetails.sortCode" wrapper-class="mb--0 mt--10"
                            size="sm" label="Sort code" info-type="optional"
                        />

                        <FormInput 
                            v-if="accountInfoByCurrency['routingNumber']"
                            id="routingNumber" v-model="accountDetails.routingNumber" 
                            wrapper-class="mt--10 mb--0"
                            size="sm" info-type="required" label="Routing Number"
                        />
                        <FormInput 
                            v-if="accountInfoByCurrency['iban']"
                            id="iban" v-model="accountDetails.iban" 
                            wrapper-class="mt--10 mb--0"
                            size="sm" label="IBAN" info-type="optional"
                        />
                    </template>

                    <template v-else>
                        <Alert type="danger" classes="text--sm mb--0">
                            <template #text>
                                <p class="text--xs">
                                    Selected currency not supported at this time.
                                </p>
                            </template>
                        </Alert>
                    </template>
                </form>
            </template>
            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton
                        class="mr--5" 
                        label="Cancel"
                        variant="outline" 
                        :disabled="loadingState === 'updateAccountLoading'"
                        @submit="closeAccountModal"
                    />
                    <BaseButton 
                        label="Add account" 
                        :disabled="accountDisabled" 
                        :loading="loadingState === 'updateAccountLoading'"
                        @submit="updateAccounts" 
                    />
                </div>
            </template>
        </MainModal>

        <MainModal 
            :show-modal="modalState === 'showDiscountModal'" 
            size="sm" 
            title="Manage discount"
            @close="modalState = ''"
        >
            <template #body>
                <form>
                    <div class="w-100">
                        <FormLabel title="Discount Type" font-size="text--sm" />
                        <select v-model="discountType.type" class="form-select form-select-sm w-100 text--capitalize">
                            <option v-for="dis in ['percentage', 'flat']" :key="dis" :value="dis">
                                {{ dis }}
                            </option>
                        </select>
                    </div>

                    <div class="mt--10 w-100">
                        <FormLabel title="Discount Value" font-size="text--sm" />
                        <div class="w-100">
                            <money 
                                v-if="discountType.type === 'flat'"
                                v-model="discountType.value" 
                                v-bind="moneyConfig" 
                                class="form-control form-control-sm w-100" 
                                spellcheck="false" 
                                @keydown.native="preventKeys"
                            />
                            <input-money 
                                v-else
                                v-model="discountType.value" 
                                class="form__input form-control-border-solid form-control-sm w-100" 
                                prefix="%"
                                precision="1" 
                            /> 
                        </div>
                    </div>
                </form>
            </template>
            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton
                        class="mr--5" 
                        label="Cancel"
                        variant="outline" 
                        @submit="modalState = ''"
                    />
                    <BaseButton 
                        label="Add discount" 
                        :disabled="!discountType.type || !parseFloat(discountType.value)"
                        @submit="setDiscount" 
                    />
                </div>
            </template>
        </MainModal>

        <MainModal 
            :show-modal="modalState === 'manageRemindersUpgrade'" 
            size="sm" 
            :close-btn="false" 
        >
            <template #body>
                <div class="modal--section">
                    <div class="upgrade--image">
                        <SvgAssets name="alarm" width="50px" height="50px" class="bg-white" />
                    </div>

                    <div class="upgrade--text">   
                        <div>
                            <div class="flex align-items-center mb--5">
                                <h2 class="upgrade--title mb--0">
                                    Set reminders
                                </h2>
                                <span
                                    v-if="accountPlan === 'free'" 
                                    class="status__map text--medium tag bold badge rounded-pill text--xxs tag--pink text--upper ml--10"
                                >
                                    <span 
                                        class="status__circle tag--pink__text text--xxs" 
                                    />
                                    Paid plans only
                                </span>
                            </div>
                            <p class="upgrade--subtitle error">
                                Reminders help control the frequency of email reminders your clients get for partially paid or no-show invoices.
                            </p>
                            <p class="upgrade--subtitle error">
                                To get started, go to your <strong>app settings > Payments & Billings</strong> and upgrade your account plan.
                            </p>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" 
                        variant="outline"
                        label="Cancel" 
                        @submit="modalState = ''"
                    />

                    <BaseButton 
                        type="submit" 
                        label="Go to settings" 
                        @submit="goToSettings(true)"
                    />
                </div>
            </template>
        </MainModal>

        <ProgressSuccess 
            :show-modal="modalState === 'showSuccessModal'"
            btn-cta="Close screen"
            title="Invoice sent!"
            desc="Your invoice is being processed and your client will be notified in a little bit."
            @close="closeSuccessModal"
        />   
    </div>
</template>

<script>
import PreviewInvoice from "@/components/invoices/PreviewInvoice.vue";
import {
    IconSvg, 
    CreateClientModal,
    CreateProjectModal, 
    BaseButton,
    FormLabel, 
    InputMoney, 
    MultipleBillingEmails,
    NonLinkedTabs, 
    NonLinkedTab,
    EmptyPage, 
    ManageRemindersModal,
    MainModal, 
    FormInput, 
    CustomDropdown, 
    UpgradeAccountModal,
    Alert,
    SvgAssets,
    ProgressSuccess,
} from "@/components/ui/index";
import { assembleQueryList, convertStringAmountToInt, extractCCEmails } from "@/utils/helpers";
import { billingMethods, taxTypes, mapTaxTypes } from "@/utils/dummy";
import InvoiceForm from "@/components/invoices/InvoiceForm.vue";
import { MAX_COUNT_BY_COLLECTIONS } from "@/utils/enums";
import api from "@/api/url";

export default {
    name: "CreateEditInvoice",

    components: {
        FormInput, 
        MultipleBillingEmails,
        PreviewInvoice, 
        CreateClientModal, 
        IconSvg, 
        CreateProjectModal, 
        BaseButton,
        FormLabel, 
        InputMoney, 
        SvgAssets, 
        InvoiceForm,
        EmptyPage, 
        MainModal, 
        ManageRemindersModal, 
        CustomDropdown, 
        UpgradeAccountModal,
        NonLinkedTabs, 
        NonLinkedTab,
        Alert,
        ProgressSuccess,
    },

    beforeRouteEnter(to, from, next) {
        next(async(vm) => {            
            if (to.name === "edit-invoice-view") { 
                if (!vm.$route.params.id) {
                    return false;
                }

                const storedInvoice = vm.$store.getters["invoice/selectedInvoice"];                
                let status = "draft";

                // Did this to avoid making the same request twice. The invoice details is already being fetched on the invoice details page, 
                // so we reuse that information instead
                // if the information isn't present on the store (due to a page reload or any other reason, we request it again)
                if (storedInvoice && storedInvoice?.invoice) {
                    status = vm.setInvoice(storedInvoice.invoice, vm);

                    const ccEmails = extractCCEmails(storedInvoice.invoice?.meta.ccEmails) || [];

                    vm.defaultInvoice = { 
                        ...vm.defaultInvoice,
                        ...storedInvoice.invoice,
                        currency: storedInvoice.invoice.currency?.toUpperCase(),
                        meta: {
                            ...vm.defaultInvoice.meta,
                            ...storedInvoice?.invoice.meta,
                            ccEmails,
                        },
                    };
                } else {
                    status = await vm.handleFetchInvoice(vm.$route.params.id, vm);
                }

                // vm.refreshPreviewData = true;

                if (!["draft", "failed"].includes(status)) {
                    return vm.$router.push("/dashboard/invoices");
                }

                // // update default client and project too
                // this.defaultClient = storedInvoice?.client || this.getStoredClientOnLS() || null;
                // this.defaultProject = storedInvoice?.project || this.getStoredClientOnLS() || null;
                vm.invoiceType = "edit";
            } else {
                if (["standard", "free"].includes(vm.accountPlan)) {   
                    let count = vm.$store.getters["invoice/pageData"]?.total;
                    
                    if (!count) {
                        count = await vm.handleFetchInvoiceCount();
                    }

                    const maxLimit = MAX_COUNT_BY_COLLECTIONS[vm.accountPlan].invoices?.create;
    
                    if (maxLimit !== 0 && count >= maxLimit) {
                        vm.isAllowed = true;
                        return;
                    } else {
                        vm.isAllowed = false;
                        vm.invoiceType = "new";
                    }    
                } else {
                    vm.invoiceType = "new";
                }
            };
        });
    },

    data() {
        return {
            invoiceType: "new",
            billingMethods, 
            taxTypes,
            mapTaxTypes,
            selectedBillingMethod: {
                id: "",
            },
            accountDetails: {
                accountName: "",
                bankName: "",
                accountNumber: "",
                swiftCode: "",
                routingNumber: "",
                iban: "",
            },
            config: {
                number: {
                    decimal: ".",
                    thousands: ",",          // The currency name or symbol followed by a space.
                    prefix: "",
                    suffix: "",
                    precision: 0,
                    masked: false,
                },
            },
            moneyConfig: {
                decimal: ".",
                thousands: ",",          // The currency name or symbol followed by a space.
                prefix: "",
                suffix: "",
                precision: 0,
                masked: false,
            },
            invoiceTaxType: { /** * @type{"percentage" | "flat"} */
                type: "",
                value: 0,
                name: "",
            },
            discountType: { /** * @type{"percentage" | "flat"} */
                type: "",
                value: "",
            },
            loadingState: "",
            innerTempEmails: [],
            selectedTaxHeader: "Add new tax",
            form: {
                selectedBillingMethod: {
                    id: "",
                },
                linkData: {
                    url: "",
                    name: "",
                },
                accountDetails: {
                    accountName: "",
                    bankName: "",
                    accountNumber: "",
                    swift: "",
                    routingNumber: "",
                },
            },
            upgradeField: "",
            providers: ["Paystack", "Flutterwave", "Stripe"],
            addLinkState: "default",
            modalState: "",
            defaultClient: null,
            defaultProject: null,
            defaultInvoice: this.generateInitialInvoiceData(),
            addClientsEmails: false,
            isAllowed: false,
            fetchInvoiceLoading: false,
            fetchInvoiceErr: false,
            billingInfoData: {
                paymentLink: null,
                accountDetails: null,
                automatic: null,
            },
            previewInvoiceData: undefined,
            refreshPreviewData: false,
        };
    },

    computed: {
        accountInfoByCurrency() {
            if (!this.selectedBillingMethod?.id === "personal_account") {
                return null;
            }

            switch (this.defaultInvoice.currency.toLowerCase()) {
            case "ngn":
            case "ghs":
                return {
                    accountName: true,
                    bankName: true,
                    accountNumber: true,
                };

            case "usd":
                return {
                    accountName: true,
                    bankName: true,
                    accountNumber: true,
                    routingNumber: true,
                    swiftCode: false,
                };

            case "gbp":
                return {
                    accountName: true,
                    bankName: true,
                    accountNumber: true,
                    iban: true,
                    swiftCode: false,
                    sortCode: false,
                };
            
            default:
                return null;
            }
        },

        addTaxDisabled() {
            return !this.invoiceTaxType.name || !this.invoiceTaxType.type || !parseFloat(this.invoiceTaxType.value);
        },

        billingMethodSelectionBtn() {
            if (!this.form.selectedBillingMethod?.name) return true;

            if (this.form.selectedBillingMethod?.id === "payment_link") {
                if (!this.form.linkData.name || !this.form.linkData.url) return true;
            }

            if (this.form.selectedBillingMethod?.id === "personal_account") {
                if (!this.accountDetails.accountName || !this.accountDetails.accountNumber) return true;
            }

            return false;
        },

        accountDisabled() {
            if (this.form.selectedBillingMethod?.id === "personal_account") {
                if (!this.accountInfoByCurrency) return true;

                let formFields = [];

                Object.entries(this.accountInfoByCurrency || {}).filter(x => {
                    if (x?.length && x[1]) {
                        formFields.push(x[0]);
                    }
                });
                
                let invalidValueCount = 0;

                for (let i = 0; i < formFields.length; i++) {
                    if (!this.accountDetails[formFields[i]]) {
                        invalidValueCount++;
                    }
                }

                if (invalidValueCount > 0) {
                    return true;
                }
            }

            return false;
        },

        user() {
            return this.$store.getters["auth/profile"];
        },

        userPaymentLinks() {
            return this.$store.getters["auth/profile"]?.paymentLinks || [];
        },

        userAccounts() {
            return this.$store.getters["auth/profile"]?.accounts || [];
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        invoicesPageData() {
            return this.$store.getters["invoice/pageData"];
        },

        checkUpgradeLoading() {
            return this.$store.getters["app/checkUpgradeLoading"];
        },

        checkUpgradeFields() {
            return this.$store.getters["app/checkUpgradeFields"];
        },

        storedInvoice() {
            return this.$store.getters["invoice/selectedInvoice"];
        },

        accountDetailsByCurrency() {
            switch (this.defaultInvoice.currency.toLowerCase()) {
            case "ngn":
            case "ghs":
                return {
                    accountName: "",
                    bankName: "",
                    accountNumber: "",
                };

            case "gbp":
            case "eur":
                return {
                    accountName: "",
                    bankName: "",
                    accountNumber: "",
                    iban: "",
                    swiftCode: "",
                };
            case "usd":
                return {
                    accountName: "",
                    bankName: "",
                    accountNumber: "",
                    routingNumber: "",
                    swiftCode: "",
                };
            
            default:
                return {
                    accountName: "",
                    bankName: "",
                    accountNumber: "",
                };
            }
        },

        taxHeaderList() {
            return [
                { name: "Add new tax", iconFill: "#212529", iconName: "alarm", showCount: false, count: undefined },
                { name: "All added taxes", iconFill: "#212529", iconName: "report", showCount: true, count: this.defaultInvoice?.meta?.tax?.length || 0 },
            ];
        },

        invoices() {
            return this.$store.getters["invoice/invoices"] || [];
        },
    },

    watch: {
        "modalState": {
            async handler(val) {
                if (val === "showCCEmailsModal") {
                    if (localStorage.getItem("z_invoice_ongoing") && localStorage.getItem("z_invoice_ongoing") !== "undefined") {
                        const data = JSON.parse(localStorage.getItem("z_invoice_ongoing"));
                        this.innerTempEmails = [ ...this.innerTempEmails, ...(data?.meta?.ccEmails || [])];
                    }
                }

                if (val === "showPaymentInfoModal") {
                    if (!this.form.selectedBillingMethod?.id) {
                        const method = this.billingMethods.find(x => x.id === this.defaultInvoice?.meta?.billingMethod);

                        if (method) {
                            this.form.selectedBillingMethod = method;

                            if (method.id === "personal_account") {
                                const accountDetails = this.userAccounts?.find(x => x._id === this.defaultInvoice.accountDetailsId);

                                if (accountDetails) {
                                    this.form.accountDetails = accountDetails;
                                }
                            }

                            if (method.id === "payment_link") {
                                const linkData = this.user?.paymentLinks?.find(x => x._id === this.defaultInvoice?.paymentLinkId);

                                if (linkData) {
                                    this.form.linkData = linkData;
                                }
                            }
                        }
                    }
                }
            },
        },

        "defaultInvoice.meta.billingMethod": {
            handler(newVal) {                
                if (newVal && newVal?.id) {
                    this.setBillingInfoData(newVal?.id);
                }
            },
            deep: true,
            immediate: true,
        },

        "defaultInvoice.currency": {
            handler(newVal) {                
                if (newVal) {                    
                    this.setBillingInfoData(this.defaultInvoice.meta.billingMethod);
                }
            },
            deep: true,
            immediate: true,
        },
    },

    async mounted() {      
        if (!this.defaultClient) {
            this.setClient(this.getStoredClientOnLS());
        }

        if (!this.defaultProject) {
            this.setProject(this.getStoredProjectOnLS());        
        }

        if (!this.defaultInvoice?.currency) {
            this.defaultInvoice = {
                ...this.defaultInvoice,
                ...(localStorage.getItem("z_invoice_ongoing") ? JSON.parse(localStorage.getItem("z_invoice_ongoing")) : {}),
            };
        }

        this.selectedBillingMethod = this.billingMethods.find(x => x.id === this.defaultInvoice.meta.billingMethod) || {};
        this.setBillingInfoData(this.defaultInvoice.meta.billingMethod);
    },

    beforeUnmount() {
        ["z_invoice_ongoing", "z_invoice_client", "z_invoice_project"].forEach((x) => {
            localStorage.removeItem(x);
        });
        this.defaultInvoice = this.generateInitialInvoiceData();
        this.defaultClient = null;
        this.defaultProject = null;
    },

    methods: {
        convertStringAmountToInt,

        goToSettings(isModal = false) {
            if (isModal) {
                this.modalState = "";
            }
            this.$router.push("/dashboard/settings/payments-and-billings");
        },

        setBillingInfoData(newVal) {            
            switch (newVal) {
            case "payment_link":
                this.billingInfoData.paymentLink = this.user?.paymentLinks?.find(x => x._id === this.defaultInvoice.paymentLinkId) || {
                    _id: "",
                    name: "",
                    url: "",
                };

                break;

            case "personal_account":
                this.billingInfoData.accountDetails = this.user?.accounts?.find(x => x._id === this.defaultInvoice.accountDetailsId) || this.accountDetailsByCurrency;

                break;
                
            case "automatic":
                this.billingInfoData.automatic = true;
                break;
                
            default:
                break;
            }
        },

        setBillingMethod(val) {
            if (!val.plans.includes(this.accountPlan)) return;

            this.form.selectedBillingMethod = val;
        },

        generateInitialInvoiceData() {
            return {
                invoiceNo: "",
                currency: "",
                amountPaid: null,
                createdAt: null,
                dueDate: null,
                _id: undefined,
                memo: "",
                meta: {
                    ccEmails: [],
                    // tax: [{ taxName: "vat", taxType: "flat", taxValue: 0 }],
                    tax: [],
                    reminders: [],
                    items: [{ itemName: "", itemPrice: 0.00, itemQuantity: 0, itemUnit: 0.00 }],
                    discount: {
                        type: "percentage",
                        value: "",
                    },
                    billingMethod: "personal_account",                
                },
                status: "draft",
                title: "Invoice for",
                accountDetailsId: "",
                paymentLinkId: "",
                deadlineType: "",
            };
        },

        getStoredClientOnLS() {
            if (localStorage.getItem("z_invoice_client") && localStorage.getItem("z_invoice_client") !== "undefined") { // for "undefined" local storage value
                const currentClient = JSON.parse(localStorage.getItem("z_invoice_client"));
                return currentClient;
            }
        },

        getStoredProjectOnLS() {
            if (localStorage.getItem("z_invoice_project") && localStorage.getItem("z_invoice_project") !== "undefined") {
                const currentProject = JSON.parse(localStorage.getItem("z_invoice_project"));
                return currentProject;
            }
        },

        async handleFetchInvoiceCount() { 
            try {                
                return await api.invoice.count();
            } catch (error) {                
                return 0;
            }
        },

        setLinkData(val) {
            if (!val) return;

            this.form.linkData = val;
            this.defaultInvoice.paymentLinkId = val._id;
        },

        updateLinks() {
            if (this.linkData?.name) {
                this.form.linkData = this.linkData;
            }
            
            this.selectedBillingMethod = this.form.selectedBillingMethod;            
            this.defaultInvoice.meta.billingMethod = this.form.selectedBillingMethod;

            if (this.form.linkData?.url) {
                this.billingInfoData.paymentLink = this.form.linkData;
            }

            // this.setBillingInfoData(this.defaultInvoice.meta.billingMethod);
            this.modalState = "";
        },

        setAccount(val) {            
            if (!val) return;
            this.accountDetails = val;
            this.defaultInvoice.accountDetailsId = val._id;
        },

        computeTaxValue(tax) {
            if (tax.taxType === "percentage") {
                return `${tax.taxValue}%`;
            }
            return `${this.$country.getSymbol(this.defaultInvoice.currency, "currency")}${this.$format_amount(tax.taxValue)}`;
        },

        closeCCEmails() {
            this.innerTempEmails = [];
            this.modalState = "";
        },

        updateCCEmails() {
            this.defaultInvoice.meta.ccEmails = this.innerTempEmails;
            this.innerTempEmails = [];
            this.modalState = "";
        },

        removeEmail(email) {
            this.innerTempEmails = this.innerTempEmails.filter(item => item !== email);
        },

        addEmail(value = "") {
            const exisitingEmail = this.innerTempEmails.find(x => x === value);
            const clientEmail = this.defaultClient?.email === value;

            if (exisitingEmail || clientEmail) return;

            this.innerTempEmails = [ ...this.innerTempEmails, value];
        },

        toggleBatchClientEmails(event) {
            const checked = event.target.checked;
            const splitClientCCEmails = this.defaultClient?.ccEmails ? this.defaultClient.ccEmails.split(",") : [];
            const ccEmails = this.getStoredClientOnLS()?.ccEmails || [];
            
            if (checked) {
                this.innerTempEmails = [ ...this.innerTempEmails, ...splitClientCCEmails, ...ccEmails ];
            } else {
                if (splitClientCCEmails.length) {
                    this.innerTempEmails = this.innerTempEmails.filter(x => !splitClientCCEmails.includes(x));
                }

                if (ccEmails.length) {
                    this.innerTempEmails = this.innerTempEmails.filter(x => !ccEmails.includes(x));
                }
            }
        },

        setDiscount() {            
            if (!this.discountType.type || !this.discountType.value) {
                this.$toast.error("Please select a valid discount type and value.");
                return;
            }

            this.defaultInvoice.meta.discount = this.discountType;
            this.modalState = "";
        },
    
        closeUpgradeModal() {
            this.$store.dispatch("app/closeUpgradeModal", this.createModalType);
        },

        async createNewClient({ payload, resetFn }) {
            try {    
                this.loadingState = "createClientLoading";
                
                const resp = await this.$store.dispatch("clients/addClient", payload);
                
                this.defaultClient = resp;
                this.$toast.success("Client created");
                resetFn();
                this.modalState = ""; 
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;

                if (errMsg.includes("upgrade your account")) {
                    this.modalState = "showUpgradeModal"; 
                    return;
                }

                this.$toast.error(errMsg);
            } finally {
                this.loadingState = "";
            }
        },

        async onProjectComplete({ payload, resetFn }) {    
            try {                
                this.loadingState = "createProjectLoading";

                const resp = await this.$store.dispatch("project/addProject", { payload} );
                
                this.$toast.success("Project created");
                this.setProject({ ...payload, ...resp });

                resetFn();
                this.modalState = ""; 
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.loadingState = "";
            }
        },

        getCurrentLSData() {
            const lsDataString = localStorage.getItem("z_invoice_ongoing");
            let lsDataObj;

            if (lsDataString && lsDataString !== "undefined") {
                lsDataObj = JSON.parse(lsDataString);    
            }

            return lsDataObj;
        },

        setClient(data = undefined) {     
            if (!data) return;

            this.defaultClient = data;

            if (data?.ccEmails) {                
                this.defaultInvoice.meta.ccEmails = extractCCEmails(data.ccEmails);
            } else {
                this.defaultInvoice.meta.ccEmails = [];
            }
        },

        setProject(data = undefined) {
            if (!data) return;

            localStorage.setItem("z_invoice_project", JSON.stringify(data));
            localStorage.setItem("z_invoice_ongoing", JSON.stringify({
                ...this.getCurrentLSData(),
                project: data,
            }));

            this.defaultProject = data;
        },

        openAccountModal() {
            const accountsCount = this.userAccounts?.length || 0;

            if (accountsCount >= 5) {
                this.$toast.error("Max accounts reached. Please delete an existing account to add a new one.");
                return;
            }

            this.modalState = "";
            this.modalState = "showAddAccountModal";
        },

        closeAccountModal() {
            this.modalState = "";
            this.modalState = "showPaymentInfoModal";
        },

        openLinkModal() {
            const linksCount = this.userPaymentLinks?.length || 0;

            if (linksCount >= 5) {
                this.$toast.error("Max payment links reached. Please delete an existing link to add a new one.");
                return;
            }

            this.modalState = "";
            this.linkData = {
                url: "",
                name: "",
            };
            this.modalState = "showAddLinkModal";
        },

        closeLinkModal() {
            // this.selectedBillingMethod = this.billingMethods.find(x => x.id === "payment_link");
            this.modalState = "";
            this.modalState = "showPaymentInfoModal";
        },

        async updatePaymentLinks() {
            try {    
                if (!this.linkData.name || !this.linkData.url ) {
                    this.$toast.error("Invalid request");
                    return;
                }

                const existingUrl = this.userPaymentLinks.find(x => x.url === this.linkData.url);
                
                if (existingUrl && existingUrl._id) {
                    this.$toast.error("A payment link with this URL already exists");
                    return;
                }

                this.loadingState = "updateLinkLoading";        
                
                const res = await this.$store.dispatch("auth/updatePaymentLinks", { payload: this.linkData, type: "add" }); 

                const freshLink = res.find(x => x.url === this.linkData.url) || res[0] || {}; 
                
                this.form.linkData =  freshLink;                  
                this.defaultInvoice.paymentLinkId = this.form.linkData._id;   
                
                this.updateAccountDetails();                          
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.loadingState = "";   
            }
        },

        async updateAccounts() {
            try {    
                if (!this.accountDetails.accountName || !this.accountDetails.accountNumber) {
                    this.$toast.error("Please fill in account details");
                    return;
                }

                const existingAccount = this.userAccounts.find(x => x.accountNumber === this.accountDetails.accountNumber);
                
                if (existingAccount && existingAccount._id) {
                    this.$toast.error("An account with this account number already exists");
                    return;
                }

                this.loadingState = "updateAccountLoading";      
                
                const res = await this.$store.dispatch("auth/updatePaymentAccounts", { payload: this.accountDetails, type: "add" });   
                
                this.accountDetails = res.find(x => x.accountNumber === this.accountDetails.accountNumber) || res[0] || {};  
                
                this.defaultInvoice.accountDetailsId = this.accountDetails._id;                 
                this.updateAccountDetails();                              
            } catch (error) {                
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            } finally {
                this.loadingState = "";   
            }
        },

        updateAccountDetails() {            
            this.selectedBillingMethod = this.form.selectedBillingMethod;            
            this.defaultInvoice.meta.billingMethod = this.form.selectedBillingMethod;
            this.modalState = "";
        },

        preventKeys(event) {
            if ((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105) || event.keyCode == 8 || event.keyCode == 9 || event.keyCode == 190 || event.keyCode == 37 || event.keyCode == 39) {
            } else {
                return event.preventDefault();
            }
        },

        setReminders(event, item) {
            const checked = event.target.checked;

            if (checked) {
                this.defaultInvoice.meta.reminders.push({
                    remindersName: item.id,
                    reminderDate: new Date(),
                });
            } else {
                this.defaultInvoice.meta.reminders = this.defaultInvoice.meta.reminders.filter(x => {
                    return x.remindersName !== item.id;
                });
            } 
        },

        addTaxToInvoice() {
            const payload = {
                taxName: this.invoiceTaxType.name,
                taxType: this.invoiceTaxType.type,
                taxValue: typeof this.invoiceTaxType.value === "string" ? parseFloat(this.invoiceTaxType.value) : this.invoiceTaxType.value,
            };
            
            this.defaultInvoice.meta.tax = [ ...this.defaultInvoice.meta.tax, payload ];
            this.invoiceTaxType = {
                type: "",
                value: 0,
                name: "",
            };
            this.modalState = "";
        }, 

        manageReminders(data) {
            this.defaultInvoice.meta.reminders = data;
            this.modalState = "";
        },

        removeTaxItem(index = this.defaultInvoice.meta.tax.length - 1) {
            this.defaultInvoice.meta.tax = this.defaultInvoice.meta.tax.filter((elem, idx) => idx !== index);
        },

        onSaveComplete() {            
            this.$router.push({ path: "/dashboard/invoices" });
        },

        onSendComplete() {    
            try {
                this.modalState = "showSuccessModal";
    
                if (this.invoices.length === 1) {
                    this.$store.dispatch("app/getOnboardingChecks").catch(e => console.log("caught", e));
                }
    
                this.$router.push({ path: "/dashboard/invoices" });
            } catch (error) {
                
            }        
        },

        setInvoice(data) {
            if (!data) return;

            const ccEmails = extractCCEmails(data?.meta.ccEmails);

            this.defaultInvoice = { 
                ...this.defaultInvoice,
                ...data,
                currency: data.currency?.toUpperCase(),
                meta: {
                    ...this.defaultInvoice.meta,
                    ...data.meta,
                    ccEmails,
                },
            };

            this.selectedBillingMethod = this.billingMethods.find(x => x.id === data.meta?.billingMethod);
            
            localStorage.setItem("z_invoice_ongoing", JSON.stringify({
                ...this.defaultInvoice,
                client: this.defaultClient,
                project: this.defaultProject,
                linkData: this.linkData,
                accountDetails: this.accountDetails,
            }));
            
            if (!this.defaultClient?._id) {
                this.setClient(data.client);

                if (data.client) {
                    this.isShowClientInfoView = true;
                }
            }

            if (!this.defaultProject?._id) {
                this.setProject(data.project);

                if (data.project) {
                    this.isShowProjectInfoView = true;
                }
            }

            return data?.status;
        },

        async handleFetchInvoice(id, vm) { 
            const scopedThis = vm || this;
            
            try {                
                scopedThis.fetchInvoiceLoading = true;
                scopedThis.fetchInvoiceErr = false;
                
                const invoiceId = id || scopedThis.$route.params.id;
                const res = await scopedThis.$store.dispatch("invoice/getInvoice", { id: invoiceId });
                                
                return scopedThis.setInvoice(res, vm);
            } catch (error) {                
                scopedThis.fetchInvoiceErr = true;
            } finally {
                scopedThis.fetchInvoiceLoading = false;
            }
        },

        closeSuccessModal() {
            this.modalState = "";
            return this.$router.push({ path: "/dashboard/invoices" });
        },
    },
};
</script>